import type { MyProfile } from './useMyProfile'

interface Auth0Name {
  given_name?: string
  name?: string
}

type NameFields = Pick<MyProfile, 'nickname' | 'firstName' | 'lastName'>

function legalName(profile: NameFields | null): string {
  if (!profile) return ''
  return [profile.firstName, profile.lastName]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' ')
}

/**
 * The name to greet a member by. Client twin of climberDisplayName() in
 * workers/api/src/utils.ts — same nickname → first+last precedence, so the
 * dashboard and the standings table can never disagree about who you are.
 *
 * The profiles row is authoritative: it is what the member edits and what every
 * other surface renders. Auth0 is a fallback only, for the first login before
 * useProfileSync has backfilled the row.
 */
export function memberDisplayName(
  profile: NameFields | null,
  auth0: Auth0Name | undefined,
  fallback: string,
): string {
  const nickname = profile?.nickname?.trim()
  if (nickname) return nickname
  return legalName(profile) || auth0?.given_name || auth0?.name || fallback
}

/**
 * The account's legal name, for the profile header — no nickname, because that
 * card is asking "whose account is this" and the nickname sits in its own field
 * right below. Mirrors fullName() in apps/gym/src/features/competitions/types.ts.
 */
export function memberFullName(
  profile: Pick<MyProfile, 'firstName' | 'lastName'> | null,
  auth0: Auth0Name | undefined,
  fallback: string,
): string {
  return (
    legalName(profile ? { ...profile, nickname: null } : null) ||
    auth0?.name ||
    auth0?.given_name ||
    fallback
  )
}
